import { useLanguage } from '../context/LanguageContext';
import { SectionHero } from '../components/sections/SectionHero';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { ArrowRight, CreditCard, Users, Calendar } from 'lucide-react';

const MembresPage = () => {
  const { t, language } = useLanguage();

  const benefits = {
    ca: [
      'Accés il·limitat al camp',
      'Descomptes al restaurant i proshop',
      'Participació en torneigs socials',
      'Prioritat en reserves',
      'Accés a clubs amb conveni'
    ],
    es: [
      'Acceso ilimitado al campo',
      'Descuentos en restaurante y proshop',
      'Participación en torneos sociales',
      'Prioridad en reservas',
      'Acceso a clubs con convenio'
    ],
    fr: [
      'Accès illimité au parcours',
      'Réductions au restaurant et au proshop',
      'Participation aux tournois sociaux',
      'Priorité dans les réservations',
      'Accès aux clubs partenaires'
    ],
    en: [
      'Unlimited course access',
      'Restaurant and proshop discounts',
      'Social tournament participation',
      'Priority bookings',
      'Access to partner clubs'
    ]
  };

  return (
    <main data-testid="membres-page">
      <SEO title={PAGE_SEO.membres.title} description={PAGE_SEO.membres.description} />
      <SectionHero
        image="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/bsl6p7eu_ARAVELL_HERO_MEMBRES_1920x700%20%281%29.jpeg"
        title={t('membres.title')}
        subtitle={t('membres.subtitle')}
      />

      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
                {language === 'ca' ? 'Beneficis' : language === 'es' ? 'Beneficios' : language === 'fr' ? 'Avantages' : 'Benefits'}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1A1A1A] font-medium tracking-tight mb-6">
                {language === 'ca' ? 'Avantatges de ser soci' : language === 'es' ? 'Ventajas de ser socio' : language === 'fr' ? 'Avantages d\'être membre' : 'Membership benefits'}
              </h2>
              
              <ul className="space-y-3 mb-8">
                {benefits[language].map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 text-[#666666]"
                  >
                    <span className="w-2 h-2 bg-[#1A4D2E] rounded-full flex-shrink-0" />
                    {benefit}
                  </motion.li>
                ))}
              </ul>

              <a
                href="https://members.teeone.golf/aravell"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1A4D2E] text-white px-8 py-4 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
                data-testid="membres-accedir-btn"
              >
                {t('membres.accedir')}
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Feature cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="bg-[#F9F9F7] p-6 border border-[#E5E5E5]">
                <CreditCard className="w-8 h-8 text-[#1A4D2E] mb-4" />
                <h3 className="font-display text-lg text-[#1A1A1A] font-medium mb-2">
                  {language === 'ca' ? 'Gestió Online' : language === 'es' ? 'Gestión Online' : language === 'fr' ? 'Gestion en Ligne' : 'Online Management'}
                </h3>
                <p className="text-sm text-[#666666]">
                  {language === 'ca' ? 'Gestiona el teu abonament des de qualsevol lloc' : language === 'es' ? 'Gestiona tu abono desde cualquier lugar' : language === 'fr' ? 'Gérez votre abonnement de n\'importe où' : 'Manage your membership from anywhere'}
                </p>
              </div>

              <div className="bg-[#F9F9F7] p-6 border border-[#E5E5E5]">
                <Users className="w-8 h-8 text-[#1A4D2E] mb-4" />
                <h3 className="font-display text-lg text-[#1A1A1A] font-medium mb-2">
                  {language === 'ca' ? 'Comunitat' : language === 'es' ? 'Comunidad' : language === 'fr' ? 'Communauté' : 'Community'}
                </h3>
                <p className="text-sm text-[#666666]">
                  {language === 'ca' ? 'Forma part de la família Aravell' : language === 'es' ? 'Forma parte de la familia Aravell' : language === 'fr' ? 'Faites partie de la famille Aravell' : 'Be part of the Aravell family'}
                </p>
              </div>

              <div className="bg-[#F9F9F7] p-6 border border-[#E5E5E5] sm:col-span-2">
                <Calendar className="w-8 h-8 text-[#1A4D2E] mb-4" />
                <h3 className="font-display text-lg text-[#1A1A1A] font-medium mb-2">
                  {language === 'ca' ? 'Reserves Fàcils' : language === 'es' ? 'Reservas Fáciles' : language === 'fr' ? 'Réservations Faciles' : 'Easy Bookings'}
                </h3>
                <p className="text-sm text-[#666666]">
                  {language === 'ca' ? 'Reserva les teves sortides amb prioritat i sense complicacions' : language === 'es' ? 'Reserva tus salidas con prioridad y sin complicaciones' : language === 'fr' ? 'Réservez vos départs en priorité et sans complications' : 'Book your tee times with priority and hassle-free'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1A4D2E] text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-2xl sm:text-3xl font-medium mb-4">
              {language === 'ca' ? 'Tens dubtes?' : language === 'es' ? '¿Tienes dudas?' : language === 'fr' ? 'Des questions?' : 'Any questions?'}
            </h2>
            <p className="text-white/80 mb-6 max-w-md mx-auto">
              {language === 'ca' 
                ? 'Contacta amb la secretaria per rebre més informació sobre les modalitats de socis.' 
                : language === 'es'
                ? 'Contacta con la secretaría para recibir más información sobre las modalidades de socios.'
                : language === 'fr'
                ? 'Contactez le secrétariat pour plus d\'informations sur les types d\'adhésion.'
                : 'Contact the secretariat to receive more information about membership options.'}
            </p>
            <a
              href="mailto:secretaria@aravellgolfclub.com"
              className="inline-block bg-white text-[#1A4D2E] px-8 py-4 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#F0F2EF] transition-colors duration-200"
              data-testid="membres-contact-btn"
            >
              {t('nav.contacte')}
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default MembresPage;
